import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { CategoryService } from '../../services/category.service';
import { CategoryDto } from 'src/core/models/query/category.dto';
import {
  Subject,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  take,
  takeUntil,
} from 'rxjs';
import { UserDefinedCategorySelectorModel } from '../../models/user-defined-category-selector.model';
import { QueryDto } from 'src/core/models/query/query.dto';
import { BaseDropdownSelectorComponent } from 'src/ui/base-selector/base-selector.module';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { QueryItemsCategoriesTupleDto } from 'src/core/models/query/query-items-categories-tuple.dto';
import { QueryItemDto } from 'src/core/models/query/query-item.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { QueryService } from 'src/core/services/query/query.service';
import { ColorUtils } from 'src/core/helpers/color-utils';

@Component({
  selector: 'ca-user-defined-category-selector',
  templateUrl: './user-defined-category-selector.component.html',
  styleUrls: ['./user-defined-category-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserDefinedCategorySelector),
    },
  ],
  standalone: false,
})
@AutoUnsubscribe()
export class UserDefinedCategorySelector
  extends BaseDropdownSelectorComponent<UserDefinedCategorySelectorModel>
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input()
  set conversationType(val: number | null) {
    this._conversationType = val;

    this.conversationType$.next(val);
  }

  @Input()
  disableFn: (category: QueryDto) => boolean = item => false;

  @Input()
  headerInfo: string;

  @Input()
  headerInfoStyle: unknown;

  @Input()
  displayMode: 'dropdown' | 'list' = 'dropdown';

  @Input()
  showSelectAll: boolean = false;

  @Input()
  searchPlaceholder: string = '';

  get conversationType(): number | null {
    return this._conversationType;
  }

  get isListMode(): boolean {
    return this.displayMode === 'list';
  }

  get isSelectAllVisible(): boolean {
    return this.showSelectAll && !this.loading && this.visibleSelectableCount > 0;
  }

  get isListEmpty(): boolean {
    return !this.loading && this.items.length === 0;
  }

  get selectAllCheckboxId(): string {
    return `${this.listCheckboxIdPrefix}-all`;
  }

  private conversationType$: Subject<number> = new Subject();
  private _conversationType: number | null = null;

  private static listInstanceCounter = 0;
  private readonly listModePageSize = 9999;
  private readonly listCheckboxIdPrefix = `ca-udc-list-${++UserDefinedCategorySelector.listInstanceCounter}`;
  private readonly notIndexedInfoIconStyle = { color: 'rgb(255, 157, 9)' };
  private readonly indexedInfoIconStyle = { color: 'rgb(0, 0, 0)' };
  private selectedIds = new Set<number>();

  itemsPerPage: number = 10;

  isQueryPreviewLoading: boolean;
  previewQueryId: number;
  popover: NgbPopover;
  queryPreviewDataCache = new Map<number, QueryItemsCategoriesTupleDto>();
  queryPreviewDataSubscription: Subscription;

  visibleSelectableCount: number = 0;
  isAllVisibleSelected: boolean = false;
  isSomeVisibleSelected: boolean = false;

  constructor(
    private service: CategoryService,
    private crudService: CrudService,
    private queryService: QueryService,
    public colorUtils: ColorUtils,
    protected elementRef: ElementRef,
    protected ngZone: NgZone,
  ) {
    super(elementRef, ngZone);
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.isListMode) {
      this.initialLoad = false;
      this.load();
    }
  }

  ngAfterViewInit(): void {
    this.attachCategoryInput();
    this.attachConversationTypeId();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  compareFn(a: CategoryDto, b: CategoryDto): boolean {
    return a.id === b.id;
  }

  writeValue(obj: any): void {
    super.writeValue(obj);

    if (this.isListMode) {
      this.updateListSelectionState();
    }
  }

  trackById(index: number, item: UserDefinedCategorySelectorModel): number {
    return item.id;
  }

  getListItemCheckboxId(item: UserDefinedCategorySelectorModel): string {
    return `${this.listCheckboxIdPrefix}-${item.id}`;
  }

  isItemSelected(item: UserDefinedCategorySelectorModel): boolean {
    return this.selectedIds.has(item.id);
  }

  isListItemDisabled(item: UserDefinedCategorySelectorModel): boolean {
    return this.isDisabled || item.disabled;
  }

  isQueryPreviewLoadingFor(item: UserDefinedCategorySelectorModel): boolean {
    return this.isQueryPreviewLoading && item.id === this.previewQueryId;
  }

  getQueryInfoIconStyle(item: UserDefinedCategorySelectorModel): { color: string } {
    return item.lastQueryVersionIndexed ? this.indexedInfoIconStyle : this.notIndexedInfoIconStyle;
  }

  onListSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.quickSearchTermInput$.next(input.value);
  }

  onListItemChange(item: UserDefinedCategorySelectorModel, event: Event): void {
    if (this.isListItemDisabled(item)) {
      return;
    }

    const checked = (event.target as HTMLInputElement).checked;
    const remaining = this.getSelectedItems().filter(x => x.id !== item.id);

    this.value = checked ? [...remaining, item] : remaining;
    this.propagateListValue();
  }

  onSelectAllChange(event: Event): void {
    if (this.isDisabled) {
      return;
    }

    const checked = (event.target as HTMLInputElement).checked;
    const visibleSelectableItems = this.getVisibleSelectableItems();
    const selectedItems = this.getSelectedItems();

    if (checked) {
      const itemsToAdd = visibleSelectableItems.filter(x => !this.selectedIds.has(x.id));
      this.value = [...selectedItems, ...itemsToAdd];
    } else {
      const visibleIds = new Set(visibleSelectableItems.map(x => x.id));
      this.value = selectedItems.filter(x => !visibleIds.has(x.id));
    }

    this.propagateListValue();
  }

  checkOverflow(element) {
    return element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth;
  }

  toggleQueryPreviewPopover(popover: NgbPopover, query: QueryDto, event: any): boolean {
    this.previewQueryId = query.id;
    this.popover = popover;

    if (popover.isOpen()) {
      popover.close();
      this.previewQueryId = 0;
    } else {
      const data = this.queryPreviewDataCache.get(query.id);
      if (data == null) {
        this.isQueryPreviewLoading = true;
        const categoriesRequest = this.queryService.getQueryCategories();
        const queryItemsRequest = this.crudService.getById<QueryItemDto[]>(
          QueryDto,
          query.id,
          'items',
        );

        this.queryPreviewDataSubscription = combineLatest([
          categoriesRequest,
          queryItemsRequest,
        ]).subscribe(([categories, queryItems]) => {
          const previewData: QueryItemsCategoriesTupleDto = {
            queryBuilderCategoryData: categories.items,
            queryItems: queryItems,
          };

          this.queryPreviewDataCache.set(query.id, previewData);
          this.isQueryPreviewLoading = false;
          popover.open({ query: query, queryItems: queryItems, categories: categories.items });
        });
      } else {
        popover.open({
          query: query,
          queryItems: data.queryItems,
          categories: data.queryBuilderCategoryData,
        });
      }
    }

    event.stopPropagation();
    return false;
  }

  protected load(): void {
    if (this.isListMode) {
      this.loadListItems();
      return;
    }

    this.loading = true;

    this.service
      .getUserDefinedCategories(
        this.conversationType,
        this.quickSearchTerm,
        this.filters,
        this.pageIndex,
        this.itemsPerPage,
      )
      .pipe(take(1))
      .subscribe(response => {
        this.totalCount = response.totalCount;
        this.items = response.items.map(x => {
          return { ...x, disabled: this.disableFn(x) } as UserDefinedCategorySelectorModel;
        });

        this.loading = false;
      });
  }

  private loadListItems(): void {
    this.loading = true;

    this.service
      .getUserDefinedCategories(
        this.conversationType,
        this.quickSearchTerm,
        this.filters,
        0,
        this.listModePageSize,
        true,
      )
      .pipe(take(1), takeUntil(this.autoUnsubscribeNotifier))
      .subscribe({
        next: response => {
          this.totalCount = response.totalCount;
          this.items = response.items.map(x => {
            return { ...x, disabled: this.disableFn(x) } as UserDefinedCategorySelectorModel;
          });

          this.updateListSelectionState();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private getSelectedItems(): UserDefinedCategorySelectorModel[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  private getVisibleSelectableItems(): UserDefinedCategorySelectorModel[] {
    return this.items.filter(x => !x.disabled);
  }

  private propagateListValue(): void {
    this.updateListSelectionState();
    this.onChange(this.value);
    this.onTouched();
  }

  private updateListSelectionState(): void {
    this.selectedIds = new Set(this.getSelectedItems().map(x => x.id));

    const visibleSelectableItems = this.getVisibleSelectableItems();
    const selectedVisibleCount = visibleSelectableItems.filter(x =>
      this.selectedIds.has(x.id),
    ).length;

    this.visibleSelectableCount = visibleSelectableItems.length;
    this.isAllVisibleSelected =
      this.visibleSelectableCount > 0 && selectedVisibleCount === this.visibleSelectableCount;
    this.isSomeVisibleSelected = selectedVisibleCount > 0 && !this.isAllVisibleSelected;
  }

  private attachCategoryInput(): void {
    this.quickSearchTermInput$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(val => {
      this.pageIndex = 0;
      this.quickSearchTerm = val;

      this.load();
    });
  }

  private attachConversationTypeId(): void {
    this.conversationType$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(val => {
      this.pageIndex = 0;
      this.load();
    });
  }
}
